import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Stack } from "@mui/material";
import type { ReactNode } from "react";

type SortableContainerProps<T> = {
	items: T[];
	getItemId: (item: T) => string;
	onReorder: (items: T[]) => void;
	children: ReactNode;
	spacing?: number;
};

export const SortableContainer = <T,>({
	items,
	getItemId,
	onReorder,
	children,
	spacing = 0,
}: SortableContainerProps<T>) => {
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (over && active.id !== over.id) {
			const oldIndex = items.findIndex((item) => getItemId(item) === active.id);
			const newIndex = items.findIndex((item) => getItemId(item) === over.id);

			if (oldIndex !== -1 && newIndex !== -1) {
				onReorder(arrayMove(items, oldIndex, newIndex));
			}
		}
	};

	const itemIds = items.map(getItemId);

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragEnd={handleDragEnd}
		>
			<SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
				<Stack spacing={spacing}>{children}</Stack>
			</SortableContext>
		</DndContext>
	);
};
